import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Icon from '@common/components/icon';
import Dropdown from '@common/components/dropdown';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { FormLabelHookForm, Group, Row } from '@common/components/form';
import { TextareaInputHookForm as TextareaInput } from '@common/components/form/inputs/textarea/textarea';
import Confirm, { ConfirmButton } from '@common/components/confirm-button';

interface Lesson {
  title: string;
  duration: string;
  objectives: string[];
}

interface ModuleProps {
  title: string;
  duration: string;
  screens: number;
  lessons: Lesson[];
  onUpdateModule?: (newTitle: string) => void;
  onUpdateLesson?: (lessonIndex: number, updatedLesson: Lesson) => void;
  onDeleteModule?: () => void;
  onDeleteLesson?: (lessonIndex: number) => void;
}

interface ModuleFormData {
  title: string;
}

interface LessonFormData {
  title: string;
  description: string;
}

const LearningModule = ({
  title,
  duration,
  screens,
  lessons,
  onUpdateModule,
  onUpdateLesson,
  onDeleteModule,
  onDeleteLesson,
}: ModuleProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLessonEditModal, setShowLessonEditModal] = useState(false);
  const [editingLessonIndex, setEditingLessonIndex] =
    useState<number | null>(null);
  const [currentTitle, setCurrentTitle] = useState(title);
  const [currentLessons, setCurrentLessons] = useState(lessons);
  const { t } = useTranslation();

  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  useEffect(() => {
    setCurrentLessons(lessons);
  }, [lessons]);

  const { control, handleSubmit, reset } = useForm<ModuleFormData>({
    defaultValues: {
      title: currentTitle,
    },
  });

  const {
    control: lessonControl,
    handleSubmit: handleLessonSubmit,
    reset: resetLesson,
  } = useForm<LessonFormData>({
    defaultValues: {
      title: '',
      description: '',
    },
  });

  const handleEditClick = () => {
    reset({ title: currentTitle }); // Reset form with current title
    setShowEditModal(true);
  };

  const handleLessonEditClick = (lessonIndex: number) => {
    const lesson = currentLessons[lessonIndex];
    setEditingLessonIndex(lessonIndex);
    resetLesson({
      title: lesson.title,
      description: '• ' + lesson.objectives.join('\n• '),
    });
    setShowLessonEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleCloseLessonModal = () => {
    setShowLessonEditModal(false);
    setEditingLessonIndex(null);
  };

  const onSubmit = (data: ModuleFormData) => {
    setCurrentTitle(data.title); // Update local state
    if (onUpdateModule) {
      onUpdateModule(data.title);
    }
    handleCloseModal();
  };

  const onLessonSubmit = (data: LessonFormData) => {
    if (editingLessonIndex !== null) {
      const updatedLesson: Lesson = {
        ...currentLessons[editingLessonIndex],
        title: data.title,
        objectives: data.description
          .replace(/^• /, '') // Remove leading bullet if present
          .split('\n• ')
          .filter((obj) => obj.trim() !== ''),
      };

      // Update local state immediately
      const updatedLessons = [...currentLessons];
      updatedLessons[editingLessonIndex] = updatedLesson;
      setCurrentLessons(updatedLessons);

      // Call parent callback
      if (onUpdateLesson) {
        onUpdateLesson(editingLessonIndex, updatedLesson);
      }
    }
    handleCloseLessonModal();
  };

  const handleDeleteModule = () => {
    if (onDeleteModule) {
      onDeleteModule();
    }
  };

  const handleDeleteLesson = (lessonIndex: number) => {
    // Update local state immediately
    const updatedLessons = currentLessons.filter(
      (_, index) => index !== lessonIndex
    );
    setCurrentLessons(updatedLessons);

    // Call parent callback
    if (onDeleteLesson) {
      onDeleteLesson(lessonIndex);
    }
  };

  return (
    <>
      <div className="tw-flex tw-flex-col tw-bg-white tw-gap-5 tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
        {/* Module Header */}
        <div
          className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-hover:bg-gray-50 tw-transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="tw-flex tw-items-center tw-gap-3 tw-w-full">
            <div className="tw-flex tw-flex-col tw-gap-3 tw-w-full">
              <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                <div className="tw-text-sm tw-text-gray-muted tw-font-inter tw-font-semibold">
                  {t('learning:learning_module_label')}
                </div>
                <div className="tw-flex tw-flex-row tw-gap-1 tw-justify-between">
                  <div className="tw-text-xl tw-font-semibold tw-text-gray-900">
                    {currentTitle}
                  </div>
                  <Dropdown
                    className="MembershipRowDropdown"
                    alignRight
                    trigger={<Icon type="more_horiz" size={20} />}
                  >
                    <Dropdown.Item
                      icon="edit__filled"
                      onClick={handleEditClick}
                    >
                      {t('learning:learning_module_edit')}
                    </Dropdown.Item>
                    <ConfirmButton
                      danger
                      title={t('learning:module_delete_confirm_title')}
                      description={t(
                        'learning:module_delete_confirm_description'
                      )}
                      onConfirm={handleDeleteModule}
                      confirmText={t('learning:module_delete_confirm_button')}
                    >
                      <Dropdown.Item icon="delete__filled">
                        {t('learning:learning_module_delete')}
                      </Dropdown.Item>
                    </ConfirmButton>
                  </Dropdown>
                </div>
              </div>

              <div className="tw-flex tw-items-center tw-gap-3 tw-mt-1 tw-text-sm tw-text-gray-500">
                <div className="tw-flex tw-items-center tw-space-x-1">
                  <div
                    className="tw-w-4 tw-h-4 tw-rounded tw-bg-no-repeat"
                    style={{
                      backgroundImage: `url(${'/static/images/timer-default.png'})`,
                    }}
                  />
                  <span className="tw-text-base tw-text-gray-muted tw-font-inter tw-font-medium">
                    {duration}
                  </span>
                </div>
                <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />
                <div className="tw-flex tw-items-center tw-gap-1">
                  <div
                    className="tw-w-4 tw-h-4 tw-rounded tw-bg-no-repeat"
                    style={{
                      backgroundImage: `url(${'/static/images/layer-three.png'})`,
                    }}
                  />
                  <span className="tw-text-base tw-text-gray-muted tw-font-inter tw-font-medium">
                    {screens} {t('learning:learning_module_screens')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module Content */}

        <div className="tw-flex tw-flex-col tw-gap-2">
          {currentLessons.map((lesson, index) => (
            <div className="tw-p-4 tw-gap-3 tw-rounded-[8px] tw-bg-gray-50">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <div className="tw-font-medium tw-text-gray-textDark tw-text-base tw-font-inter">
                  {lesson.title}
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2 tw-text-sm tw-text-gray-500">
                  <Dropdown
                    className="MembershipRowDropdown"
                    alignRight
                    trigger={<Icon type="more_horiz" size={20} />}
                  >
                    <Dropdown.Item
                      icon="edit__filled"
                      onClick={() => handleLessonEditClick(index)}
                    >
                      {t('learning:learning_module_edit')}
                    </Dropdown.Item>
                    <Confirm
                      danger
                      title={t('learning:lesson_delete_confirm_title')}
                      description={t(
                        'learning:lesson_delete_confirm_description'
                      )}
                      onConfirm={() => handleDeleteLesson(index)}
                      confirmText={t('learning:lesson_delete_confirm_button')}
                    >
                      <Dropdown.Item icon="delete__filled">
                        {t('learning:learning_module_delete')}
                      </Dropdown.Item>
                    </Confirm>
                  </Dropdown>
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2 tw-ml-2">
                {lesson.objectives.map((objective) => (
                  <li
                    className="tw-flex tw-items-start tw-font-inter
                  tw-font-medium tw-space-x-2 tw-text-base tw-text-gray-muted"
                  >
                    <div className="tw-w-1 tw-h-1 tw-bg-gray-muted tw-rounded-full tw-mt-2 tw-flex-shrink-0" />
                    <span>{objective}</span>
                  </li>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Module Modal */}
      <ModalWindow
        show={showEditModal}
        onHide={handleCloseModal}
        size="small"
        className="EditableModule"
      >
        <ModalContent
          title={t('learning:learning_module_edit_module_title')}
          onHide={handleCloseModal}
          onConfirm={handleSubmit(onSubmit)}
          confirmButtonText={t('learning:learning_module_save_changes')}
        >
          <Row>
            <Group>
              <TextareaInput
                className="!tw-h-[100px] tw-text-base tw-font-inter tw-font-medium"
                name="title"
                control={control}
                placeholder={t('learning:learning_module_enter_module_title')}
                minRows={3}
              />
            </Group>
          </Row>
        </ModalContent>
      </ModalWindow>

      {/* Edit Lesson/Screen Modal */}
      <ModalWindow
        show={showLessonEditModal}
        onHide={handleCloseLessonModal}
        size="small"
        className="EditableScreen"
      >
        <ModalContent
          title={t('learning:learning_module_edit_screen_title')}
          onHide={handleCloseLessonModal}
          onConfirm={handleLessonSubmit(onLessonSubmit)}
          confirmButtonText={t('learning:learning_module_save_changes')}
          cancelButtonText={t('learning:learning_module_cancel')}
        >
          <Row>
            <Group>
              <FormLabelHookForm
                control={lessonControl}
                name="title"
                text={t('learning:learning_module_heading_label')}
                className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
              />
              <TextareaInput
                className="!tw-h-[100px] tw-text-base tw-font-inter tw-font-medium"
                name="title"
                control={lessonControl}
                placeholder={t('learning:learning_module_enter_screen_heading')}
                minRows={2}
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <FormLabelHookForm
                control={lessonControl}
                name="description"
                text={t('learning:learning_module_description_label')}
                className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
              />
              <TextareaInput
                className="!tw-h-[200px] tw-text-base tw-font-inter tw-font-medium"
                name="description"
                control={lessonControl}
                placeholder={t('learning:learning_module_enter_objectives')}
                minRows={8}
              />
            </Group>
          </Row>
        </ModalContent>
      </ModalWindow>
    </>
  );
};

export default LearningModule;
